import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import memberService from '../../services/memberService';
import { useTenant } from '../../contexts/TenantContext';
import ProfilePicture from '../../components/common/ProfilePicture';
import api from '../../services/api';
import { sessionState } from '../../services/api';
import authService from '../../services/authService';
import { getValidTenantId } from '../../utils/tenantUtils';

// Import Material UI components
import {
  Box, Grid, Typography, Paper, Avatar,
  TextField, InputAdornment, Table, 
  TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, Pagination, 
  CircularProgress, Alert,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Button
} from '@mui/material';

// Import Material UI icons
import {
  People as PeopleIcon,
  Search as SearchIcon,
  PersonOutline as PersonOutlineIcon,
  RefreshOutlined as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';

// Helper function to construct profile picture URL
const constructProfilePictureUrl = (profilePicturePath) => {
  if (!profilePicturePath) return null;
  
  try {
    // If it's already a full URL, return it
    if (profilePicturePath.startsWith('http')) {
      return profilePicturePath;
    }
    
    // Get base URL from environment
    let apiBaseUrl = import.meta.env.VITE_API_URL || '';
    
    // Default fallback URL
    if (!apiBaseUrl) apiBaseUrl = 'http://localhost:5000';
    
    // Remove /api suffix if present for image URLs
    apiBaseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl;
    
    // Remove trailing slash if present
    apiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    
    // Ensure the path starts with a slash
    let normalizedPath = profilePicturePath;
    if (!normalizedPath.startsWith('/')) {
      normalizedPath = '/' + normalizedPath;
    }
    
    // Handle specific path formats
    if (normalizedPath.includes('/uploads/profile-pictures/')) {
      // Path already contains the full uploads directory structure
      return `${apiBaseUrl}${normalizedPath}`;
    } else if (normalizedPath.includes('/uploads/')) {
      // Path contains uploads but not the specific subfolder
      return `${apiBaseUrl}${normalizedPath}`;
    } else {
      // Add the uploads/profile-pictures path
      normalizedPath = normalizedPath.replace(/^\//, ''); // Remove leading slash if any
      return `${apiBaseUrl}/uploads/profile-pictures/${normalizedPath}`;
    }
  } catch (e) {
    console.error('Error constructing profile picture URL:', e);
    return null;
  }
};

const MemberDirectoryPage = () => {
  const navigate = useNavigate();
  const { tenant, refreshTenant } = useTenant();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Session and tenant ID states
  const [sessionExpired, setSessionExpired] = useState(false);
  const [tenantMissing, setTenantMissing] = useState(false);
  const [errorType, setErrorType] = useState(null);

  // Check if directory is enabled - add fallback values to prevent crashes
  const isDirectoryEnabled = tenant?.settings?.directory?.enabled === true;
  const visibleFields = tenant?.settings?.directory?.visibleFields || ['firstName', 'lastName', 'membershipType', 'joinDate'];

  // Register the dialog setter with the global session state
  useEffect(() => {
    // Register our dialog setter with the global session state
    sessionState.setShowSessionDialog = setSessionExpired;
    
    // Clean up on unmount
    return () => {
      if (sessionState.setShowSessionDialog === setSessionExpired) {
        sessionState.setShowSessionDialog = null;
      }
    };
  }, []);

  // Check for tenant ID on component mount
  useEffect(() => {
    const tenantId = getValidTenantId();
    if (!tenantId) {
      console.warn('No tenant ID found on component mount');
      setTenantMissing(true);
      setErrorType('tenantMissing');
    } else {
      console.log(`Found tenant ID: ${tenantId}`);
      setTenantMissing(false);
    }
  }, []);

  // Session reload handler
  const handleSessionReload = () => {
    setSessionExpired(false);
    window.location.reload();
  };

  // Handle login redirect
  const handleLoginRedirect = () => {
    // Store current URL to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/login');
  };

  // Handle tenant redirect
  const handleTenantRedirect = () => {
    // Try to refresh tenant context first
    refreshTenant().then(success => {
      if (success) {
        // If tenant was successfully refreshed, reload the page
        window.location.reload();
      } else {
        // If not successful, redirect to tenant selection page
        navigate('/select-tenant');
      }
    });
  };

  useEffect(() => {
    const fetchDirectoryMembers = async () => {
      if (!isDirectoryEnabled) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Check for token expiration and tenant ID
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('No authentication token found');
          setSessionExpired(true);
          setErrorType('sessionExpired');
          setIsLoading(false);
          return;
        }
        
        // Check for tenant ID
        const tenantId = getValidTenantId();
        if (!tenantId) {
          console.error('No tenant ID found when trying to fetch directory members');
          setTenantMissing(true);
          setErrorType('tenantMissing');
          setIsLoading(false);
          return;
        }
        
        // If we have both token and tenantId, proceed with the request
        console.log(`Fetching directory members with tenant ID: ${tenantId}`);
        
        const response = await memberService.getDirectoryMembers(currentPage, 10, search);
        
        // Process the data
        let memberData = [];
        let pagination = { pages: 1, total: 0 };
        
        // Handle various response formats
        if (response && Array.isArray(response)) {
          memberData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          memberData = response.data;
          pagination = response.pagination || pagination;
        } else if (response && response.members && Array.isArray(response.members)) {
          memberData = response.members;
          pagination = response.pagination || pagination;
        } else {
          console.warn('Unexpected response format:', response);
          memberData = [];
        }
        
        console.log('Directory members loaded:', memberData.length);
        setMembers(memberData);
        setTotalPages(pagination.pages || Math.ceil((pagination.total || memberData.length) / 10) || 1);
        
        // Show warning for demo data
        if (response.demo) {
          setError('Using sample directory data. Authentication required for real data.');
        }
      } catch (error) {
        console.error('Error in directory component:', error);
        
        // Check if error is due to authentication/session expiration
        if (error.response && error.response.status === 401) {
          setSessionExpired(true);
          setErrorType('sessionExpired');
        } else if (error.message && error.message.toLowerCase().includes('tenant')) {
          setTenantMissing(true);
          setErrorType('tenantMissing');
        } else {
          setError(`Failed to display directory: ${error.message || 'Unknown error'}`);
        }
        
        // Fallback to empty state - don't crash the UI
        setMembers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDirectoryMembers();
  }, [isDirectoryEnabled, currentPage, search, tenant]);

  // Handle page change
  const handlePageChange = (event, page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Check if field should be visible
  const isFieldVisible = (field) => visibleFields.includes(field);

  // If directory is disabled, show a more informative message
  if (!isDirectoryEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
            color: 'white',
          }}
        >
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ mr: 1 }} /> Member Directory
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Browse the list of members in your organization
          </Typography>
        </Paper>
        
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <PeopleIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.4 }} />
          <Typography variant="h5" gutterBottom>
            Member Directory Not Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mb: 2 }}>
            The member directory feature is currently disabled by your organization administrator. 
            Please contact your administrator if you need access to this feature.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Session Expiration Dialog */}
      <Dialog
        open={sessionExpired}
        aria-labelledby="session-dialog-title"
        aria-describedby="session-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: '4px solid #f44336',
            maxWidth: 400,
            width: '100%'
          }
        }}
      >
        <DialogTitle id="session-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <RefreshIcon color="error" />
            <Typography variant="h6" component="span">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="session-dialog-description" sx={{ mb: 1 }}>
            {sessionState.sessionError || "Your session has expired. Please reload or log in again to restore access."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={handleSessionReload} 
            color="primary" 
            variant="contained" 
            startIcon={<RefreshIcon />}
            fullWidth
            sx={{ 
              py: 1, 
              borderRadius: 1.5,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 6px 10px rgba(0,0,0,0.15)',
              }
            }}
          >
            Reload Now
          </Button>
          <Button
            onClick={handleLoginRedirect}
            color="primary"
            variant="outlined"
            fullWidth
            sx={{ py: 1, borderRadius: 1.5 }}
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Missing Dialog */}
      <Dialog
        open={tenantMissing}
        aria-labelledby="tenant-dialog-title"
        aria-describedby="tenant-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: '4px solid #ff9800',
            maxWidth: 400,
            width: '100%'
          }
        }}
      >
        <DialogTitle id="tenant-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutlineIcon sx={{ color: '#ff9800' }} />
            <Typography variant="h6" component="span">
              Organization Access Required
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="tenant-dialog-description" sx={{ mb: 1 }}>
            Organization access is required to view the member directory. Please reload or select your organization to continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={handleTenantRedirect} 
            color="primary" 
            variant="contained" 
            fullWidth
            sx={{ 
              py: 1, 
              borderRadius: 1.5,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: '#ff9800',
              '&:hover': { 
                boxShadow: '0 6px 10px rgba(0,0,0,0.15)',
                bgcolor: '#f57c00' 
              }
            }}
          >
            Select Organization
          </Button>
          <Button 
            onClick={handleSessionReload}
            color="primary"
            variant="outlined"
            fullWidth
            sx={{ py: 1, borderRadius: 1.5 }}
          >
            Reload Page
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1 }} /> Member Directory
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Browse the list of members in your organization
        </Typography>
      </Paper>

      {/* Search */}
      <Paper
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          borderTop: '4px solid #2196F3'
        }}
      >
        <TextField
          fullWidth
          label="Search Members"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          placeholder="Search by name or other details"
        />
      </Paper>

      {/* Members Table */}
      <Paper
        elevation={2}
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          borderLeft: '4px solid #009688',
        }}
      >
        <Box 
          sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: 1, 
            borderColor: 'divider',
            background: 'linear-gradient(to right, rgba(0,150,136,0.05) 0%, rgba(255,255,255,0) 100%)'
          }}
        >
          <Typography variant="h5" fontWeight="medium" color="#009688">
            Member List
          </Typography>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="subtitle2">{error}</Typography>
            </Alert>
          </Box>
        ) : members.length > 0 ? (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'rgba(0,150,136,0.03)' }}>
                  <TableRow>
                    {isFieldVisible('firstName') && <TableCell>Name</TableCell>}
                    {isFieldVisible('membershipType') && <TableCell>Membership</TableCell>}
                    {isFieldVisible('joinDate') && <TableCell>Join Date</TableCell>}
                    {isFieldVisible('email') && <TableCell>Email</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => {
                    const user = member.userId;
                    
                    // Check for profile picture
                    const profilePicture = user?.profilePicture || member.profilePicture;
                    const profilePictureUrl = profilePicture ? constructProfilePictureUrl(profilePicture) : null;
                    
                    return (
                      <TableRow 
                        key={member._id}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                          '&:hover': { bgcolor: 'rgba(0,150,136,0.08)' }
                        }}
                      >
                        {isFieldVisible('firstName') && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {profilePicture ? (
                                <Avatar sx={{ mr: 2 }}>
                                  <ProfilePicture 
                                    src={profilePictureUrl}
                                    firstName={user?.firstName || ''}
                                    lastName={user?.lastName || ''}
                                    size="md"
                                    round={true}
                                  />
                                </Avatar>
                              ) : (
                                <Avatar sx={{ mr: 2, bgcolor: '#009688' }}>
                                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                                </Avatar>
                              )}
                              <Typography variant="body2" fontWeight="medium">
                                {user?.firstName} {user?.lastName}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        
                        {isFieldVisible('membershipType') && (
                          <TableCell>
                            <Chip 
                              label={member.membershipType || 'Regular'} 
                              size="small"
                              sx={{ 
                                bgcolor: 'rgba(0,150,136,0.1)',
                                color: '#00796b',
                                fontWeight: 'medium'
                              }}
                            />
                          </TableCell>
                        )}
                        
                        {isFieldVisible('joinDate') && (
                          <TableCell>{formatDate(member.joinDate)}</TableCell>
                        )}
                        
                        {isFieldVisible('email') && (
                          <TableCell>{user?.email}</TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Pagination 
                  count={totalPages} 
                  page={currentPage} 
                  onChange={handlePageChange} 
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PersonOutlineIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
            <Typography color="text.secondary">
              {search ? 'No members found matching your search.' : 'No members available in the directory.'}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MemberDirectoryPage;
